import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
    CatalogueSettings,
    CatalogueSettingsUpdate,
    AvailableCamp,
    SelectedCamp,
    CatalogueManualItem,
    AvailableContentResponse,
    NewCatalogueItem,
} from '../../public-catalogue/models/public-catalogue.models';

@Injectable({ providedIn: 'root' })
export class PublicCatalogueAdminApiService {
    private baseUrl: string;

    constructor(private http: HttpClient) {
        const apiUrl = environment.envVar.API_URL.endsWith('/')
            ? environment.envVar.API_URL.slice(0, -1)
            : environment.envVar.API_URL;
        this.baseUrl = `${apiUrl}/company`;
    }

    private url(companySlug: string): string {
        return `${this.baseUrl}/${companySlug}/public-catalogue`;
    }

    // === Settings ===

    getSettings(companySlug: string): Observable<CatalogueSettings> {
        return this.http.get<CatalogueSettings>(`${this.url(companySlug)}/settings`);
    }

    updateSettings(companySlug: string, data: CatalogueSettingsUpdate): Observable<CatalogueSettings> {
        return this.http.put<CatalogueSettings>(`${this.url(companySlug)}/settings`, data);
    }

    // === Camps ===

    getAvailableCamps(companySlug: string): Observable<AvailableCamp[]> {
        return this.http.get<{ data: AvailableCamp[] }>(`${this.url(companySlug)}/available-camps`).pipe(
            map(res => res.data)
        );
    }

    getSelectedCamps(companySlug: string): Observable<SelectedCamp[]> {
        return this.http.get<{ data: SelectedCamp[] }>(`${this.url(companySlug)}/camps`).pipe(
            map(res => res.data)
        );
    }

    addCamps(companySlug: string, campIds: number[]): Observable<any> {
        return this.http.post(`${this.url(companySlug)}/camps`, { campIds });
    }

    removeCamp(companySlug: string, campId: number): Observable<void> {
        return this.http.delete<void>(`${this.url(companySlug)}/camps/${campId}`);
    }

    // === Items (manual mode) ===

    getItems(companySlug: string, isFeatured?: boolean): Observable<CatalogueManualItem[]> {
        let params = new HttpParams();
        if (isFeatured !== undefined) {
            params = params.set('isFeatured', isFeatured.toString());
        }

        return this.http.get<{ data: CatalogueManualItem[] }>(`${this.url(companySlug)}/items`, { params }).pipe(
            map(res => res.data)
        );
    }

    addItems(companySlug: string, items: NewCatalogueItem[]): Observable<any> {
        return this.http.post(`${this.url(companySlug)}/items`, { items });
    }

    updateItem(companySlug: string, itemId: number, data: { isFeatured?: boolean; position?: number }): Observable<any> {
        return this.http.put(`${this.url(companySlug)}/items/${itemId}`, data);
    }

    deleteItem(companySlug: string, itemId: number): Observable<void> {
        return this.http.delete<void>(`${this.url(companySlug)}/items/${itemId}`);
    }

    // === Available Content ===

    getAvailableContent(companySlug: string, params?: { type?: string; q?: string; perPage?: number; page?: number }): Observable<AvailableContentResponse> {
        let httpParams = new HttpParams();
        if (params?.type) { httpParams = httpParams.set('type', params.type); }
        if (params?.q) { httpParams = httpParams.set('q', params.q); }
        if (params?.perPage) { httpParams = httpParams.set('perPage', params.perPage.toString()); }
        if (params?.page) { httpParams = httpParams.set('page', params.page.toString()); }

        return this.http.get<AvailableContentResponse[]>(`${this.url(companySlug)}/available-content`, { params: httpParams }).pipe(
            map((res: any) => Array.isArray(res) ? res[0] : res)
        );
    }
}
